import {createFileRoute} from '@tanstack/react-router';
import {useEffect, useRef, useState} from 'react';
import {apiAction, apiActionImproved, apiRetrieveImproved} from '../api';
import {ClubType, DrawType, isRacquetSport, LeagueType, ParticipantsType, UserRole} from '../constants';
import {startGoogleSignIn} from '../utilities/googleAuth.ts';
import {config} from '../config/config.ts';
import {useEventPassReturn} from '../hooks/useEventPassReturn.ts';
import {useJwtAuth, useJwtUser} from '../JWTAuthProvider';
import services from '../services';
import {isPassGate, queuePostCreateOffer} from '../pricing/organizerPathRules.ts';
import {suppressSetupTourOnce} from '../components/events/setupTourFlags.ts';
import {
    clearGeneratorHandoff,
    GeneratorHandoff,
    readGeneratorHandoff,
    stashGeneratorTournament,
} from '../start/generatorHandoff.ts';

export const Route = createFileRoute('/quiz-legacy')({
    component: QuizPage,
});

/**
 * This route is a frozen copy of the 30 July quiz, kept purely so the old and new funnels can be
 * walked side by side. It must stay inert: no Meta ad events, no Quiz Responses rows. The flag is
 * read by fireEvent() and recordResponse() below, which are otherwise identical to the live quiz.
 */
const IS_SNAPSHOT = true;

/**
 * Ad-click quiz funnel: animated logo splash → 5 questions → offer screen → tournament BUILDER
 * (name + format + teams, no account needed) → "Generate draw" gates a minimal signup (name,
 * email, password only) → tournament + teams + draw are created. Quiz builds are NOT entry-capped:
 * a quiz signup creates a model-2 club, which EntryLimitService exempts from the PAYG 8-entry cap,
 * so any field size builds free. The pass screen and the entry_cap catch branches remain as a
 * safety net for a server-side 403, but no quiz path reaches them by design.
 *
 * Offerwall routing (see gatedBuild): every non-Americano build runs behind the walled
 * /quiz/activate/ URL — with no cap gate there is no pay screen to undercut, so watching the
 * video is the one price for every field size.
 *
 * Tracking: ViewContent (quiz start), Lead (pain answer), QuizComplete (all questions answered,
 * a custom event) and InitiateCheckout (offer screen) fire as deduplicated browser+server pairs;
 * CompleteRegistration and Purchase fire server-side on the real signup and Event Pass checkout.
 */

type Step = {q: string; sub: string; options: {val: string; label: string}[]};

const STEPS: Step[] = [
    {
        q: 'What are you organizing next?',
        sub: "Takes 30 seconds — we'll set things up around your answer.",
        options: [
            {val: 'tournament', label: 'A tournament'},
            {val: 'league', label: 'A league season'},
            {val: 'ladder', label: 'A ladder or box league'},
            {val: 'social', label: 'A social event or Americano'},
        ],
    },
    {
        q: 'Which sport?',
        sub: "We'll match formats and scoring to your sport.",
        options: [
            {val: 'padel', label: 'Padel'},
            {val: 'pickleball', label: 'Pickleball'},
            {val: 'tennis', label: 'Tennis'},
            {val: 'other', label: 'Another sport'},
        ],
    },
    {
        q: "What's the biggest headache when you run events?",
        sub: 'Pick the one that costs you the most time.',
        options: [
            {val: 'payments', label: 'Chasing players for payment'},
            {val: 'signups', label: 'Collecting signups and player info'},
            {val: 'scores', label: 'Keeping scores and brackets updated'},
            {val: 'schedule', label: 'Sharing schedules and standings'},
        ],
    },
    {
        q: 'How do you handle event fees today?',
        sub: 'Be honest — this is where most organizers lose money.',
        options: [
            {val: 'cash', label: 'Cash on the day'},
            {val: 'transfer', label: 'Bank transfers I chase manually'},
            {val: 'mixed', label: "A mix — it's messy"},
            {val: 'free', label: 'My events are free to enter'},
        ],
    },
    {
        q: 'How many teams in your next event?',
        sub: 'Last question — your setup is almost ready.',
        options: [
            {val: 'small', label: 'Up to 8 teams'},
            {val: 'medium', label: '9 to 16 teams'},
            {val: 'large', label: '17 to 32 teams'},
            {val: 'xl', label: 'More than 32'},
        ],
    },
];

// Little momentum banners shown above the next question after each answer.
const UNLOCKS: Record<number, Record<string, string>> = {
    1: {
        tournament: '✓ Bracket & draw builder unlocked',
        league: '✓ Season standings & fixtures unlocked',
        ladder: '✓ Ladder challenges & rankings unlocked',
        social: '✓ Americano rotations unlocked',
    },
    2: {
        padel: '✓ Padel scoring formats loaded',
        pickleball: '✓ Pickleball scoring + DUPR support loaded',
        tennis: '✓ Tennis set scoring loaded',
        other: '✓ 15+ sports supported — scoring loaded',
    },
    3: {
        payments: '✓ Automatic payment collection added to your setup',
        signups: '✓ Public registration page added to your setup',
        scores: '✓ Live scoring & auto-updating brackets added',
        schedule: '✓ Shareable schedule & standings page added',
    },
    4: {
        cash: '✓ Online entry fees — no more cash to count',
        transfer: '✓ Automatic payment links — no more chasing',
        mixed: '✓ One place for every entry fee',
        free: '✓ Free events run free on ScorePal',
    },
};

const SPORT_NAMES: Record<string, string> = {padel: 'padel', pickleball: 'pickleball', tennis: 'tennis', other: 'your sport'};
// Stored sport_type keys (note the platform's legacy 'pickelball' spelling).
const SPORT_KEYS: Record<string, string> = {padel: 'padel', pickleball: 'pickelball', tennis: 'tennis', other: 'other'};
const EVENT_NAMES: Record<string, string> = {tournament: 'tournament', league: 'league season', ladder: 'ladder', social: 'social event'};

const PAIN_FEATURES: Record<string, string> = {
    payments: 'Entry fees collected online, upfront — payouts straight to your bank',
    signups: 'A public registration page — players sign themselves up',
    scores: 'Live scoring with brackets and standings that update themselves',
    schedule: 'A shareable page with the schedule, results and standings',
};

const FORMATS: {val: 'round_robin' | 'groups_knockout' | 'knockout' | 'americano'; label: string; desc: string}[] = [
    {val: 'round_robin', label: 'Round Robin', desc: 'Everyone plays everyone.'},
    {val: 'groups_knockout', label: 'Groups + Knockout', desc: 'Group stage, then a final bracket.'},
    {val: 'knockout', label: 'Knockout', desc: 'Single elimination bracket.'},
    {val: 'americano', label: 'Americano', desc: 'Social mixer — players swap partners each round, ranked individually.'},
];

const T_UID_KEY = 'sp_quiz_tuid';
const DRAW_DONE_KEY = 'sp_quiz_draw_done';
const STARTED_KEY = 'sp_quiz_started';
const PASS_OK_KEY = 'sp_quiz_pass_ok';
const USERS_DONE_KEY = 'sp_quiz_users_done';
const R_KEY = 'sp_quiz_rkey';
const RECOVERY_KEY = 'sp_quiz_recovery';

/**
 * Should this build run behind the Offerwall activation URL? Every field size: quiz builds are
 * uncapped (a quiz signup's club is model-2, exempt from the PAYG entry cap), so no build can
 * reach a pay screen and watching a video IS the price. Americano is always excluded: it has its
 * own activation flow at /americanos/activate/.
 */
const gatedBuild = (h: GeneratorHandoff): boolean =>
    h.eventKind !== 'americano';

// Real testimonials from the homepage TestimonialsSection (not invented copy) — auto-rotated.
const TESTIMONIALS = [
    {
        quote: 'Since introducing ScorePal at CF Tennis, participation in our leagues has noticeably increased. Players are more motivated knowing every match result instantly impacts the live standings. It\'s simple for our team to manage and brings extra energy to every competition.',
        name: 'Clark F.',
        role: 'Director, CF Tennis Academy',
    },
    {
        quote: 'ScorePal has completely elevated how we run leagues and tournaments at Love the Padel. Instead of manually tracking results and standings, everything updates automatically with live leaderboards our players can check in real time.',
        name: 'Alex S.',
        role: 'Founder, Love the Padel',
    },
    {
        quote: 'Inputting scores takes seconds and the live updates keep everyone in the loop. It actually adds a professional feel to our regular padel games and fuels the competitive spirit. I can\'t imagine going back to the spreadsheet I was using before!',
        name: 'Paul G.',
        role: 'Padel Chairman',
    },
];

// Module-scoped so StrictMode's dev remount (which resets refs) can't start a second concurrent
// build — the duplicate loops would double-add every team.
let createRunning = false;

// Splash decision survives StrictMode's double-effect (which would otherwise see the just-written
// "seen" flag on its re-run and skip the splash entirely). null = not decided this page load.
let splashDecided: boolean | null = null;

// The async build outlives component instances (StrictMode mounts twins; auth changes can remount).
// Its UI updates go through this hook, re-registered by whichever instance is currently mounted,
// so progress and the pay/cap gates always render instead of landing on an unmounted twin.
let ui: {step: (n: number) => void; status: (s: string) => void; cap: (c: any) => void; err: (m: string) => void; done: (dest: string) => void} | null = null;

// Post-trim recovery: when the user declines the pass at the cap gate and trims to the free 8,
// the ORIGINAL field is kept here (with the created event's uid) so the done screen can offer the
// pass to restore the cut names. Expires after 48h so a stale offer never shadows a newer build.
type RecoveryStash = {uid: string; handoff: GeneratorHandoff; kept: number; ts: number};

function readRecovery(): RecoveryStash | null {
    try {
        const raw = localStorage.getItem(RECOVERY_KEY);
        if (!raw) return null;
        const rec = JSON.parse(raw) as RecoveryStash;
        if (!rec?.uid || !rec?.handoff?.teams?.length || !(rec.kept >= 1) || Date.now() - (rec.ts || 0) > 48 * 3600 * 1000) {
            localStorage.removeItem(RECOVERY_KEY);
            return null;
        }
        return rec;
    } catch { return null; }
}

function readCookie(name: string): string | null {
    const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
}

function uuid(): string {
    return (crypto as any).randomUUID ? (crypto as any).randomUUID() :
        'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.random() * 16 | 0;
            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
}

// Browser pixel + server CAPI with a shared event_id (Meta dedupes on name+id). The CAPI relay is
// gated on the _fbp cookie: it only exists once the pixel has actually run, i.e. consent granted.
//
// DISABLED on this route. /quiz-legacy is a July-30 snapshot kept only for funnel comparison —
// firing real ad conversions from a test bench would corrupt Meta's campaign optimisation.
function fireEvent(name: string, customData: Record<string, any> = {}, dedupeKey = name, custom = false) {
    if (IS_SNAPSHOT) return;
    const fired: string[] = JSON.parse(sessionStorage.getItem('sp_quiz_fired') || '[]');
    if (fired.includes(dedupeKey)) return;
    sessionStorage.setItem('sp_quiz_fired', JSON.stringify([...fired, dedupeKey]));

    const eventId = uuid();
    const w = window as any;
    // Custom (non-standard) events must use trackCustom so Meta doesn't warn/misclassify them.
    if (w.fbq) w.fbq(custom ? 'trackCustom' : 'track', name, customData, {eventID: eventId});

    const fbp = readCookie('_fbp');
    if (!fbp) return; // pixel hasn't run (no consent yet) — skip the server relay too

    fetch(config.apiUrl + '/quiz/event', {
        method: 'POST',
        headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
        body: JSON.stringify({
            event_name: name,
            event_id: eventId,
            custom_data: customData,
            source_url: window.location.href.split('#')[0],
            fbp,
            fbc: readCookie('_fbc'),
        }),
        keepalive: true, // survives the redirect to signup/checkout
    }).catch(() => {});
}

// Full-funnel answer log (Admin → Quiz Responses): upserts one row per visitor after every answer,
// so abandoned quizzes are recorded too. The JWT (present from signup onwards) links the row to the
// account; the tournament uid is attached once the build creates it.
//
// DISABLED on this route — see fireEvent above. Test-bench walkthroughs must not land in the
// Quiz Responses dashboard alongside real funnel data.
function recordResponse(payload: Record<string, any> = {}) {
    if (IS_SNAPSHOT) return;
    let key = localStorage.getItem(R_KEY);
    if (!key) {
        key = uuid();
        localStorage.setItem(R_KEY, key);
    }
    const token = localStorage.getItem('jwt_token');
    fetch(config.apiUrl + '/quiz/response', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(token ? {Authorization: 'Bearer ' + token} : {}),
        },
        body: JSON.stringify({key, source_url: window.location.href.split('#')[0], ...payload}),
        keepalive: true,
    }).catch(() => {});
}

// Fire the "your event is ready" email the moment a basic-mode quiz event is built (the scheduled
// events:welcome-emails command is the backstop). Fire-and-forget — never blocks the redirect.
function sendQuizWelcomeEmail(payload: {tournament_uid?: string; league_uid?: string}) {
    const token = localStorage.getItem('jwt_token');
    fetch(config.apiUrl + '/quiz/event-welcome', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(token ? {Authorization: 'Bearer ' + token} : {}),
        },
        body: JSON.stringify(payload),
        keepalive: true,
    }).catch(() => {});
}

export function QuizPage() {
    const jwt = useJwtAuth();
    const user = useJwtUser();

    // This component serves both /quiz/ and /quiz/activate/ (the Offerwall-gated activation URL —
    // see quiz.activate.tsx). Free builds only run on the activate path; getting there is always a
    // REAL page load so the AdSense Offerwall evaluates its URL rules.
    const onActivatePath = typeof window !== 'undefined' && window.location.pathname.includes('/quiz/activate');

    // step: 0..4 questions, 5 offer, 6 builder, 7 signup, 8 creating, 9 event-pass required
    const [phase, setPhase] = useState<'splash' | 'quiz'>('splash');
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState<Record<string, string>>(() => {
        try { return JSON.parse(sessionStorage.getItem('sp_quiz_answers') || '{}'); } catch { return {}; }
    });
    const [unlock, setUnlock] = useState('');

    // Builder state
    const [title, setTitle] = useState('');
    // The last title WE generated — so a back-and-change of sport regenerates it without ever
    // overwriting a title the user typed themselves.
    const autoTitle = useRef('');
    const [mode, setMode] = useState<'basic' | 'advanced'>('basic');
    // Staged "preparing…" beat between the setup form and the account screen (logged-out only).
    const [prepStage, setPrepStage] = useState<number | null>(null);
    const [playType, setPlayType] = useState<'singles' | 'doubles'>('doubles');
    const [format, setFormat] = useState<'round_robin' | 'groups_knockout' | 'knockout' | 'americano'>('round_robin');
    const [leagueFormat, setLeagueFormat] = useState<'singles' | 'doubles_individual' | 'teams'>('doubles_individual');
    const [groupCount, setGroupCount] = useState(2);
    const [teamsText, setTeamsText] = useState('');
    const [builderError, setBuilderError] = useState('');

    // Live platform stats for the offer screen's trust strip (same public endpoint as the homepage).
    const [stats, setStats] = useState<{matches: number; players: number; clubs: number; communities: number} | null>(null);

    // Rotating testimonial on the final "setup is ready" screen.
    const [tIdx, setTIdx] = useState(0);
    useEffect(() => {
        if (step !== 10) return;
        const iv = setInterval(() => setTIdx((i) => (i + 1) % TESTIMONIALS.length), 6000);
        return () => clearInterval(iv);
    }, [step]);

    // Signup state
    const [suName, setSuName] = useState('');
    const [suEmail, setSuEmail] = useState('');
    const [suPassword, setSuPassword] = useState('');
    const [suError, setSuError] = useState('');
    const [suBusy, setSuBusy] = useState(false);

    // Creation state
    const [status, setStatus] = useState('');
    const [capInfo, setCapInfo] = useState<{cap: number; count: number; reason: 'cap' | 'plan'} | null>(null);
    const [payBusy, setPayBusy] = useState(false);
    // Post-trim pass offer on the done screen (read fresh each time step 10 shows).
    const [recovery, setRecovery] = useState<RecoveryStash | null>(null);
    const [doneDest, setDoneDest] = useState(''); // where the "setup is ready" CTA goes (draws / league / setup)

    const sportKey = SPORT_KEYS[answers['2']] || 'padel';
    // League answer builds a league (individual standings, no draw); everything else stays a tournament.
    const isLeague = answers['1'] === 'league';
    // Americano is a self-contained format with its own dedicated setup (players, courts, points),
    // so the quiz hands off to /americanos/create rather than building a draw here.
    const isAmericano = !isLeague && format === 'americano';
    const rawLines = teamsText.split('\n').map((t) => t.trim()).filter(Boolean);

    // Doubles entry, mobile-friendly: one player per line and every two names auto-pair — no "&"
    // typing needed. A line that already contains a pair separator (&, +, /, "and") stays as-is.
    // League singles is the only per-player entry; league doubles and teams enter pairs too.
    const pairEntry = isLeague ? leagueFormat !== 'singles' : playType === 'doubles';
    let leftoverSingle: string | null = null;
    let teamNames: string[];
    if (pairEntry) {
        const pairs: string[] = [];
        let pending: string | null = null;
        for (const line of rawLines) {
            if (/[&+/]|\band\b/i.test(line)) {
                pairs.push(line);
            } else if (pending) {
                pairs.push(`${pending} & ${line}`);
                pending = null;
            } else {
                pending = line;
            }
        }
        leftoverSingle = pending;
        teamNames = pairs;
    } else {
        teamNames = rawLines;
    }

    // × on a chip: rebuild the textarea from the remaining entries (auto-paired names become
    // explicit "A & B" lines, which parse back identically).
    const removeEntry = (idx: number) => {
        const remaining = teamNames.filter((_, i) => i !== idx);
        setTeamsText([...remaining, ...(leftoverSingle ? [leftoverSingle] : [])].join('\n'));
    };

    // Register this instance as the build's UI sink (last mounted wins — see the module comment).
    useEffect(() => {
        ui = {step: setStep, status: setStatus, cap: setCapInfo, err: setBuilderError,
              done: (dest: string) => { setDoneDest(dest); setStep(10); }};
    });

    // Splash: play once per visitor, skip on repeat visits. The decision is made once per page
    // load (module flag) so StrictMode's double-effect neither strands nor skips it; the timer is
    // rescheduled by the effect re-run after cleanup.
    useEffect(() => {
        if (splashDecided === null) splashDecided = !localStorage.getItem('sp_splash_seen');
        localStorage.setItem('sp_splash_seen', '1');
        if (!splashDecided) {
            setPhase('quiz');
            return;
        }
        const t = setTimeout(() => {
            splashDecided = false; // played — in-session revisits skip it
            setPhase('quiz');
        }, 7500);
        return () => clearTimeout(t);
    }, []);

    // Restore the builder state from a stash so counts and copy on later screens read correctly.
    const restoreFromHandoff = (stash: GeneratorHandoff) => {
        setTitle(stash.title || '');
        setFormat((stash.format as any) || 'round_robin');
        setTeamsText((stash.teams || []).join('\n'));
        if (stash.groupCount) setGroupCount(stash.groupCount);
        if (stash.playType === 'singles' || stash.playType === 'doubles') setPlayType(stash.playType);
        if (stash.leagueFormat) setLeagueFormat(stash.leagueFormat);
        if (stash.mode === 'advanced') setMode('advanced');
    };

    // Resume: after the signup thank-you redirect (authed, stash present) or the Event Pass return,
    // jump straight back into the build instead of replaying the questions.
    useEffect(() => {
        if (!jwt.initialized || phase !== 'quiz') return;
        const stash = readGeneratorHandoff();
        if (localStorage.getItem(STARTED_KEY) !== '1' || !stash || stash.source !== 'quiz') return;
        restoreFromHandoff(stash);
        const passReturn = new URLSearchParams(window.location.search).get('event_pass') === 'success';
        if (passReturn) { setStep(8); setStatus('Confirming your Event Pass…'); return; } // useEventPassReturn resumes
        if (user) {
            // Back from "Continue with Google" with a bare socialite account — finish the organizer
            // signup (role + community) first, then build. Otherwise the account is complete: build.
            if (+user.role === UserRole.EmptyAfterSignup) { completeGoogleSignup(stash); }
            else if (gatedBuild(stash) && !onActivatePath && localStorage.getItem(PASS_OK_KEY) !== '1') {
                // Guaranteed-free build resuming on /quiz/ — route through the Offerwall activation
                // URL first. Pass-paid builds (PASS_OK) and 9+ fields (cap gate ahead) skip it.
                window.location.assign('/quiz/activate/');
            }
            else { runCreate(stash); }
        }
        else { setStep(7); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jwt.initialized, phase, user?.uid]);

    // Event Pass Stripe return: confirm + apply the pass to the stored event, then resume.
    // The stored uid is a league ('el-…') on the league route — apply must use the matching key.
    // Mid-build (T_UID_KEY still set) resumes the build; otherwise this is a post-trim recovery
    // purchase from the done screen — restore the saved overflow to the finished event.
    const storedUid = typeof window !== 'undefined' ? localStorage.getItem(T_UID_KEY) : null;
    const passUid = storedUid || (typeof window !== 'undefined' ? readRecovery()?.uid || null : null);
    useEventPassReturn(passUid ? (passUid.startsWith('el-') ? {league_uid: passUid} : {tournament_uid: passUid}) : null, () => {
        if (storedUid) {
            localStorage.setItem(PASS_OK_KEY, '1'); // paid + applied — don't gate the draw again
            const stash = readGeneratorHandoff();
            if (stash) runCreate(stash);
            return;
        }
        const rec = readRecovery();
        if (rec) runRecovery(rec);
    });

    // Recovery purchase return: the build already finished, so no stash/started flag exists and the
    // resume effect above won't fire — park on the progress screen and restore the ORIGINAL field so
    // the done screen reads the full count once runRecovery lands there.
    useEffect(() => {
        if (phase !== 'quiz') return;
        if (new URLSearchParams(window.location.search).get('event_pass') !== 'success') return;
        if (localStorage.getItem(STARTED_KEY) === '1') return; // mid-build return — resume effect handles it
        const rec = readRecovery();
        if (!rec) return;
        restoreFromHandoff(rec.handoff);
        setStep(8);
        setStatus('Confirming your Event Pass…');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase]);

    // AdSense Offerwall: /quiz/activate/ is targeted in AdSense (Privacy & messaging → Offerwall →
    // URL inclusions), so free builds get gated with a video there. The tag must be loaded
    // explicitly — the app-shell loader skips it for organizers (open gating marks them
    // subscribed) — and in free mode this ad IS the payment, hence loading before the build runs.
    useEffect(() => {
        if (!onActivatePath || !config.adsenseEnabled || typeof document === 'undefined'
            || window.location.hash === '#no-tracking') {
            return;
        }
        const scriptId = 'adsbygoogle-' + config.adsenseClient;
        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.async = true;
            script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${config.adsenseClient}`;
            script.crossOrigin = 'anonymous';
            document.head.appendChild(script);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Funnel events + the offer screen's live trust stats.
    useEffect(() => {
        if (phase === 'quiz' && step === 0) fireEvent('ViewContent', {content_name: 'quiz_start'});
        // The pass offer now only appears at the entry-cap gate (step 9) — that's the checkout intent.
        if (step === 9) fireEvent('InitiateCheckout', {value: 4.99, currency: 'USD'});
        // Post-trim recovery offer: read fresh on every arrival at the done screen — runRecovery
        // clears the key before landing here, so a completed restore hides the card automatically.
        if (step === 10) setRecovery(readRecovery());
        if (step === 10 && !stats) {
            fetch(config.apiUrl + '/public-stats', {headers: {Accept: 'application/json'}})
                .then((r) => (r.ok ? r.json() : null))
                .then((d) => { if (d && typeof d.players === 'number') setStats(d); })
                .catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase, step]);

    const answer = (stepNo: number, val: string) => {
        const next = {...answers, [String(stepNo)]: val};
        setAnswers(next);
        sessionStorage.setItem('sp_quiz_answers', JSON.stringify(next));
        recordResponse({answers: next, completed: Object.keys(next).length >= STEPS.length});
        if (stepNo === 3) fireEvent('Lead', {content_category: val});
        setUnlock(UNLOCKS[stepNo]?.[val] || '');
        if (stepNo === 5) startBuilder(next); // last question answered — straight into the builder
        else setStep(stepNo);
    };

    // Last answer → builder, seeded from the quiz answers. Everyone starts on the free plan; the
    // Event Pass is offered at the entry-cap gate when the field actually needs it.
    const startBuilder = (finalAnswers: Record<string, string>) => {
        sessionStorage.setItem('sp_quiz_plan', 'free');
        const sportName = SPORT_NAMES[finalAnswers['2']] || '';
        // Quiz complete — all questions answered, entering the builder. A distinct custom event
        // (not CompleteRegistration, which fires server-side on real signup) so quiz-start →
        // quiz-complete is a clean, measurable funnel step for ad optimization.
        fireEvent('QuizComplete', {
            content_name: 'quiz_complete',
            content_category: finalAnswers['2'] || '',
            event_type: isLeague ? 'league' : 'tournament',
        }, 'QuizComplete', true);
        const sportPrefix = sportName ? sportName.charAt(0).toUpperCase() + sportName.slice(1) + ' ' : '';
        // Regenerate when empty or still OUR auto-title — a back-and-change of sport used to
        // keep the old sport's name on the new sport's event. User-typed titles never match.
        if (!title || title === autoTitle.current) {
            const t = `My ${sportPrefix}${isLeague ? 'League' : 'Tournament'}`;
            setTitle(t);
            autoTitle.current = t;
        }
        // "A social event or Americano" at step 1 lands straight on the Americano format.
        setFormat(finalAnswers['1'] === 'social' ? 'americano' : finalAnswers['5'] === 'small' ? 'round_robin' : 'groups_knockout');
        setPlayType(finalAnswers['2'] === 'tennis' ? 'singles' : 'doubles');
        setLeagueFormat(finalAnswers['2'] === 'tennis' ? 'singles' : 'doubles_individual');
        setStep(6);
    };

    // "Generate draw" — the signup gate. Stash the build so it survives redirects, then either
    // create right away (already signed in) or ask for the minimal account first.
    const generateDraw = () => {
        // Basic mode generates a draw and needs a field; Advanced goes to the full setup page, so
        // entrants can be added later. Americano collects its players on its own setup screen, so it
        // needs no names here.
        if (!isAmericano && mode === 'basic' && teamNames.length < 2) {
            setBuilderError(pairEntry
                ? 'Add at least 4 players (one name per line — every two make a pair).'
                : 'Add at least 2 players (one per line).');
            return;
        }
        setBuilderError('');
        const stash: GeneratorHandoff = {
            source: 'quiz',
            format,
            sport: sportKey,
            teams: teamNames,
            title: title.trim() || (isAmericano ? 'My Americano' : isLeague ? 'My League' : 'My Tournament'),
            groupCount,
            qualifiers: 2,
            playType,
            mode,
            eventKind: isAmericano ? 'americano' : isLeague ? 'league' : 'tournament',
            leagueFormat,
        };
        stashGeneratorTournament(stash);
        localStorage.setItem(STARTED_KEY, '1');
        // A fresh build supersedes any old post-trim offer — but an error-retry of the SAME build
        // (same stored uid) must keep the blob, or the trim recovery dies with the retry click.
        const rec = readRecovery();
        if (rec && rec.uid !== localStorage.getItem(T_UID_KEY)) localStorage.removeItem(RECOVERY_KEY);
        if (user) {
            // Guaranteed-free builds run behind the Offerwall activation URL (real page load — the
            // wall only evaluates its URL rules on a page view). A 9+ field builds right here and
            // meets the cap gate un-walled.
            if (gatedBuild(stash) && !onActivatePath) {
                window.location.assign('/quiz/activate/');
                return;
            }
            runCreate(stash);
        }
        else {
            // Logged-out: walk the prep stages, then land on the account screen.
            setPrepStage(0);
            [1, 2].forEach((s) => setTimeout(() => setPrepStage(s), s * 1300));
            setTimeout(() => { setPrepStage(null); setStep(7); }, 2 * 1300 + 1400);
        }
    };

    // Minimal signup: name, email, password. Sport/community are derived from the quiz so the
    // account form stays three fields.
    const submitSignup = async (e: any) => {
        e.preventDefault();
        if (!suName.trim() || !suEmail.trim() || !suPassword) { setSuError('Please fill in every field.'); return; }
        setSuBusy(true); setSuError('');
        try {
            const res: any = await apiAction('signup', {
                type: 'organizer',
                landing_url: jwt.landingUrl,
                platform: 'web',
                profile: {email: suEmail.trim(), password: suPassword, name: suName.trim(), lastname: '', inviter_uid: ''},
                questionnaire: {
                    sport: sportKey,
                    elo_grade: null, how_long_playing: null, how_often: null, nationality: null,
                    // No audience question in this funnel: the community default, sent explicitly so
                    // the club counts as having answered and is never stopped mid-funnel to be asked.
                    club: {type: ClubType.Community, name: `${suName.trim()}'s Events`, country_code: '', city: '', city_coordinates: null, meta: {}, organizer_path: 'community'},
                },
            });
            if (res?.error) { setSuError(res.error); setSuBusy(false); return; }
            jwt.setFieldsFromResponse(res);
            recordResponse({}); // token is stored now — links this quiz row to the new account
            // Quiz signups already have their competition mid-build — the "create your first
            // competition" Get Started popup would interrupt the handoff, so mark it seen.
            sessionStorage.setItem('welcome_tutorial_seen', '"y"'); // useSessionStorage stores JSON
            if (res?.user?.uid) {
                apiActionImproved('users-update', {
                    uid: res.user.uid,
                    values: {version: (res.user.version || 0) + 1, ['meta->welcome_tutorial_seen']: true},
                }).catch(() => {});
            }
            // Fire the signup conversion inline (same events the /signup/thank-you/ page sends) —
            // navigating there would remount this page and orphan the in-flight build.
            const gtag = (window as any).gtag;
            if (typeof gtag === 'function' && window.location.hash !== '#no-tracking') {
                gtag('event', 'sign_up', {method: 'scorepal', signup_type: 'organizer'});
                gtag('event', 'conversion', {send_to: 'AW-17042305006/ec_JCKW6_8ocEO7fs74_'});
            }
            const stash = readGeneratorHandoff();
            // Freshly signed up on /quiz/ — a guaranteed-free build runs behind the Offerwall
            // activation URL, so hand over with a real page load (gtag beacons survive the unload).
            if (stash && gatedBuild(stash) && !onActivatePath) {
                window.location.assign('/quiz/activate/');
                return;
            }
            if (stash) runCreate(stash);
        } catch (err: any) {
            setSuError(err?.message || 'Sign up failed. Please try again.');
            setSuBusy(false);
        }
    };

    // "Continue with Google" return leg: the socialite callback created a bare EmptyAfterSignup
    // account — upgrade it to organizer (community derived from their Google name, sport from the
    // quiz) with the same bookkeeping as the password signup, then continue the build.
    const googleCompleting = useRef(false);
    const completeGoogleSignup = async (stash: GeneratorHandoff) => {
        if (googleCompleting.current || !user) return;
        googleCompleting.current = true;
        setStep(8);
        setStatus('Finishing your account…');
        try {
            const res: any = await apiAction('signup', {
                type: 'organizer',
                landing_url: jwt.landingUrl,
                platform: 'web',
                profile: {email: user.email, password: '', name: user.name || '', lastname: '', inviter_uid: ''},
                questionnaire: {
                    sport: stash.sport || sportKey,
                    elo_grade: null, how_long_playing: null, how_often: null, nationality: null,
                    // Same as the password signup above: explicit community, never asked mid-funnel.
                    club: {type: ClubType.Community, name: `${(user.name || 'My').trim()}'s Events`, country_code: '', city: '', city_coordinates: null, meta: {}, organizer_path: 'community'},
                },
            });
            if (res?.error) throw new Error(res.error);
            jwt.setFieldsFromResponse(res);
            recordResponse({}); // token now maps this quiz row to the completed account
            sessionStorage.setItem('welcome_tutorial_seen', '"y"'); // useSessionStorage stores JSON
            if (res?.user?.uid) {
                apiActionImproved('users-update', {
                    uid: res.user.uid,
                    values: {version: (res.user.version || 0) + 1, ['meta->welcome_tutorial_seen']: true},
                }).catch(() => {});
            }
            const gtag = (window as any).gtag;
            if (typeof gtag === 'function' && window.location.hash !== '#no-tracking') {
                gtag('event', 'sign_up', {method: 'google', signup_type: 'organizer'});
                gtag('event', 'conversion', {send_to: 'AW-17042305006/ec_JCKW6_8ocEO7fs74_'});
            }
            runCreate(stash);
        } catch (err: any) {
            setSuError(err?.message || 'Sign up failed. Please try again.');
            setStep(7);
            googleCompleting.current = false;
        }
    };

    // The draw runs on name-based teams, but each typed person should also exist as a real player
    // account (owner_uid = organizer) so they show on the organizer's Players page and count in the
    // admin panel — the same accounts the full UI creates. The accounts are then LINKED to their
    // team (p1/p2) so the Setup → Players tab shows who is in each pair instead of blank rows.
    // dedupe_by_name makes re-runs (retries, event-pass returns, recovery) reuse the same accounts.
    // Non-critical: a failed profile never blocks the draw.
    const ensurePlayerProfiles = async (uid: string, data: GeneratorHandoff) => {
        ui?.status('Creating player profiles…');
        const people = new Map<string, string>();
        data.teams
            .flatMap((line) => line.split(/[&+/]|\band\b/i).map((s) => s.trim()).filter(Boolean))
            .forEach((n) => people.set(n.toLowerCase(), n));
        const uidByName = new Map<string, string>();
        for (const name of people.values()) {
            const [first, ...rest] = name.split(/\s+/);
            const p: any = await services.players.createAndAdd({first_name: first, last_name: rest.join(' '), display_name: name, dedupe_by_name: true}).catch(() => null);
            if (p?.uid) uidByName.set(name.toLowerCase(), p.uid);
        }
        localStorage.setItem(USERS_DONE_KEY, '1');

        ui?.status('Linking players to their teams…');
        const created: any[] = await services.tournaments.teams(uid).catch(() => []);
        for (const t of (created || [])) {
            if (t.p1_uid) continue; // already linked (resume)
            const persons = String(t.name || '').split(/[&+/]|\band\b/i).map((s) => s.trim()).filter(Boolean);
            const p1 = uidByName.get((persons[0] || '').toLowerCase());
            const p2 = persons[1] ? uidByName.get(persons[1].toLowerCase()) : undefined;
            if (!p1) continue;
            await apiActionImproved('teams-update', {
                uid: t.uid,
                values: {version: (t.version || 0) + 1, p1_uid: p1, ...(p2 ? {p2_uid: p2} : {})},
            }).catch(() => {});
        }
    };

    // Generate the draw(s) matching the chosen format, sized to data.teams.
    const generateDrawsFor = async (uid: string, data: GeneratorHandoff) => {
        ui?.status('Generating the draw…');
        const count = data.teams.length;
        const current: any = await services.tournaments.find(uid);
        await services.tournaments.update(uid, {version: (current?.version || 1) + 1} as any);
        const draw = async (params: any) => {
            const r: any = await services.tournaments.createDraw({uid, draw: params} as any);
            // Keep the structured body (its `code`): a pass gate is handed over to the event
            // page by the caller, never shown as a message.
            if (r?.error) throw Object.assign(new Error(r.error), r);
        };
        if (data.format === 'knockout') {
            await draw({draw_type: DrawType.Elimination, teams_count: count, winners_count: 1, pools_count: 1, playoffs_count: 2, create_consolation_draw: false, disable_shuffle: false});
        } else if (data.format === 'round_robin') {
            await draw({draw_type: DrawType.RoundRobin, teams_count: count, winners_count: 1, pools_count: 1, playoffs_count: 0, create_consolation_draw: false, disable_shuffle: false});
        } else {
            const pools = Math.max(1, Math.min(data.groupCount || 2, Math.floor(count / 2)));
            await draw({draw_type: DrawType.RoundRobin, teams_count: count, winners_count: 1, pools_count: pools, playoffs_count: 0, create_consolation_draw: false, disable_shuffle: false});
            await draw({draw_type: DrawType.Elimination, teams_count: Math.max(2, pools * (data.qualifiers || 2)), winners_count: 1, pools_count: 1, playoffs_count: 2, create_consolation_draw: false, disable_shuffle: false});
        }
    };

    // Post-trim recovery: the user finished free with the capped field, then bought the pass from
    // the done screen. The pass is already applied (useEventPassReturn) — add the saved overflow
    // back and, as long as no score has been recorded meanwhile, rebuild the draw for the full field.
    const runRecovery = async (rec: RecoveryStash) => {
        if (createRunning) return;
        createRunning = true;
        const h = rec.handoff;
        const uid = rec.uid;
        const overflow = h.teams.slice(rec.kept);
        ui?.step(8);
        try {
            if (h.eventKind === 'league') {
                const isTeams = h.leagueFormat === 'teams';
                ui?.status(isTeams ? 'Adding your saved teams…' : 'Adding your saved players…');
                const have = new Set<string>();
                if (isTeams) {
                    const existing: any[] = await apiRetrieveImproved('leagues-teams', {league_uid: uid}).catch(() => []) as any;
                    (existing || []).forEach((t: any) => have.add(String(t.name || '').trim().toLowerCase()));
                } else {
                    const existing: any[] = await services.leagues.players(uid).catch(() => []);
                    (existing || []).forEach((p: any) => {
                        [p.display_name, `${p.name || ''} ${p.lastname || ''}`.trim()]
                            .filter(Boolean).forEach((n: string) => have.add(n.trim().toLowerCase()));
                    });
                }
                const entrantNames = isTeams || h.leagueFormat === 'singles'
                    ? overflow
                    : overflow.flatMap((line) => line.split(/[&+/]|\band\b/i).map((s) => s.trim()).filter(Boolean));
                for (const raw of entrantNames) {
                    const name = raw.trim();
                    if (!name || have.has(name.toLowerCase())) continue;
                    if (isTeams) {
                        await apiActionImproved('teams-create', {league_uid: uid, name});
                    } else {
                        const [first, ...rest] = name.split(/\s+/);
                        await services.players.createAndAdd({first_name: first, last_name: rest.join(' '), display_name: name, league_uid: uid});
                    }
                }
                localStorage.removeItem(RECOVERY_KEY);
                ui?.done(`/leagues/${uid}/league?welcome=quiz`);
                return;
            }

            ui?.status('Adding your saved teams…');
            const existing: any[] = await services.tournaments.teams(uid).catch(() => []);
            const have = new Set((existing || []).map((t: any) => String(t.name || '').trim().toLowerCase()));
            for (const raw of overflow) {
                const name = raw.trim();
                if (!name || have.has(name.toLowerCase())) continue;
                await apiActionImproved('teams-create', {tournament_uid: uid, name});
            }
            if (h.playType !== 'teams') {
                await ensurePlayerProfiles(uid, h);
            }

            // Rebuild the draw for the full field — but never wipe a recorded score: if any game
            // already has a result, leave the stages alone (the field is in; the organizer can
            // regenerate from the Draws tab).
            const games: any[] = await services.games.get({tournament_uid: uid}).catch(() => []);
            const played = (games || []).some((g: any) => g?.sets?.length);
            if (!played) {
                ui?.status('Rebuilding the draw…');
                const st: any = await services.tournaments.stages(uid).catch(() => null);
                for (const stage of (st?.stages || [])) {
                    await apiAction('tournament-draw-delete', {draws_stage_uid: stage.uid});
                }
                await generateDrawsFor(uid, h);
            }
            localStorage.removeItem(RECOVERY_KEY);
            ui?.done(`/tournaments/${uid}/draws?welcome=quiz`);
        } catch {
            // Paid but the rebuild hit a snag — the pass is applied and (some) teams are in, so
            // land on the event rather than stranding a paying user on the quiz. The recovery blob
            // stays put, so revisiting /quiz within 48h offers the restore again.
            window.location.href = h.eventKind === 'league' ? `/leagues/${uid}/league` : `/tournaments/${uid}/draws`;
        } finally {
            createRunning = false;
        }
    };

    // League build (quiz "league" answer): a Win% league with individual or team standings — no
    // draw to generate; standings build as scores are recorded. Same idempotent shape as the
    // tournament path: stored uid, name-dedupe, entry-cap gate, Event Pass gate.
    const runCreateLeague = async (data: GeneratorHandoff) => {
        ui?.status('Creating your league…');
        let uid = localStorage.getItem(T_UID_KEY) || '';
        if (!uid) {
            const res: any = await apiActionImproved('leagues-create', {
                title: data.title || 'My League',
                sport_type: data.sport || 'padel',
                league_type: LeagueType.WinPercent,
                // Scheduled mode: entrants are already in, so organizers get the Setup → Schedule
                // tab to generate rounds (bespoke leagues hide it; switchable in Setup → Basic).
                is_auto_scheduled: true,
                participants_type: data.leagueFormat === 'teams' ? ParticipantsType.DoublesTeam
                    : data.leagueFormat === 'singles' ? ParticipantsType.Singles
                        : ParticipantsType.DoublesIndividual,
                sets_count: 1,
            });
            if (!res?.uid) throw new Error(res?.error || res?.message || 'Could not create the league');
            uid = res.uid;
            localStorage.setItem(T_UID_KEY, uid);
            recordResponse({tournament_uid: uid, mode: data.mode});
        }

        const isTeams = data.leagueFormat === 'teams';
        ui?.status(isTeams ? 'Adding your teams…' : 'Adding your players…');
        const have = new Set<string>();
        if (isTeams) {
            const existing: any[] = await apiRetrieveImproved('leagues-teams', {league_uid: uid}).catch(() => []) as any;
            (existing || []).forEach((t: any) => have.add(String(t.name || '').trim().toLowerCase()));
        } else {
            const existing: any[] = await services.leagues.players(uid).catch(() => []);
            (existing || []).forEach((p: any) => {
                [p.display_name, `${p.name || ''} ${p.lastname || ''}`.trim()]
                    .filter(Boolean).forEach((n: string) => have.add(n.trim().toLowerCase()));
            });
        }
        // Teams become fixed pairs by their line ("Anna & Ben"); doubles-individual splits each
        // pair line back into two individually-ranked players; singles lines are single players.
        const entrantNames = isTeams || data.leagueFormat === 'singles'
            ? data.teams
            : data.teams.flatMap((line) => line.split(/[&+/]|\band\b/i).map((s) => s.trim()).filter(Boolean));
        for (const raw of entrantNames) {
            const name = raw.trim();
            if (!name || have.has(name.toLowerCase())) continue;
            try {
                if (isTeams) {
                    await apiActionImproved('teams-create', {league_uid: uid, name});
                } else {
                    const [first, ...rest] = name.split(/\s+/);
                    await services.players.createAndAdd({first_name: first, last_name: rest.join(' '), display_name: name, league_uid: uid});
                }
            } catch (err: any) {
                if (err?.code === 'entry_cap') {
                    ui?.cap({cap: err.cap || 8, count: entrantNames.length, reason: 'cap'});
                    ui?.step(9);
                    createRunning = false;
                    return;
                }
                throw err;
            }
        }

        // Invite-to-earn plan: the full field is in (the pledge lifted the cap) — hand straight
        // over to Setup → Players so the organizer starts inviting. No draw exists on a league,
        // so nothing else is gated here.
        if (sessionStorage.getItem('sp_quiz_plan') === 'earn') {
            clearGeneratorHandoff();
            [T_UID_KEY, DRAW_DONE_KEY, STARTED_KEY, PASS_OK_KEY, USERS_DONE_KEY].forEach((k) => localStorage.removeItem(k));
            createRunning = false;
            suppressSetupTourOnce(); // sent to Players on purpose — the help guide waits
            window.location.assign(`/leagues/${uid}/setup/applications/`);
            return;
        }

        // Pass plan: collect payment before showing the league, mirroring the tournament path.
        if (sessionStorage.getItem('sp_quiz_plan') === 'pass' && localStorage.getItem(PASS_OK_KEY) !== '1') {
            ui?.status('Checking your Event Pass…');
            const st = await services.eventPass.status().catch(() => null);
            if (st && st.available > 0) {
                await services.eventPass.apply({league_uid: uid}).catch(() => {});
                localStorage.setItem(PASS_OK_KEY, '1');
            } else {
                ui?.cap({cap: 8, count: data.teams.length, reason: 'plan'});
                ui?.step(9);
                createRunning = false;
                return;
            }
        }

        clearGeneratorHandoff();
        [T_UID_KEY, DRAW_DONE_KEY, STARTED_KEY, PASS_OK_KEY, USERS_DONE_KEY].forEach((k) => localStorage.removeItem(k));
        if (data.mode !== 'advanced') sendQuizWelcomeEmail({league_uid: uid}); // fully set up — email now
        ui?.done(data.mode === 'advanced' ? `/leagues/${uid}/setup` : `/leagues/${uid}/league?welcome=quiz`);
    };

    // Idempotent create: reuses the stored tournament, only adds missing teams, generates the draw
    // once — safe to re-run after the signup redirect or the Event Pass round-trip.
    const runCreate = async (data: GeneratorHandoff) => {
        if (createRunning) return;
        // Americano has its own dedicated setup (players, courts, points, payment) — the quiz just
        // funnels the (now signed-in) organizer straight there rather than building anything.
        if (data.eventKind === 'americano') {
            clearGeneratorHandoff();
            [T_UID_KEY, DRAW_DONE_KEY, STARTED_KEY, PASS_OK_KEY, USERS_DONE_KEY].forEach((k) => localStorage.removeItem(k));
            window.location.href = '/americanos/create';
            return;
        }
        createRunning = true;
        ui?.step(8);
        try {
            if (data.eventKind === 'league') {
                await runCreateLeague(data);
                return;
            }
            ui?.status('Creating your tournament…');
            let uid = localStorage.getItem(T_UID_KEY) || '';
            if (!uid) {
                const res: any = await services.tournaments.create({
                    title: data.title || 'My Tournament',
                    sport_type: data.sport || 'padel',
                    tournament_type: data.format === 'knockout' ? 2 : 1,
                    draws_description: data.format === 'groups_knockout' ? 'Round Robin + Elimination'
                        : data.format === 'knockout' ? 'Elimination' : 'Round Robin',
                    // singles = 3, doubles/pairs = 1, whole teams = 2 (entrants stay name-based)
                    participants_type: data.playType === 'singles' ? 3 : data.playType === 'doubles' ? 1 : 2,
                    sets_count: 1,
                    visibility_type: 1,
                } as any);
                if (!res?.uid) throw new Error(res?.error || res?.message || 'Could not create the tournament');
                uid = res.uid;
                localStorage.setItem(T_UID_KEY, uid);
                recordResponse({tournament_uid: uid, mode: data.mode});
            }

            ui?.status('Adding your teams…');
            const existing: any[] = await services.tournaments.teams(uid).catch(() => []);
            const have = new Set((existing || []).map((t: any) => String(t.name || '').trim().toLowerCase()));
            for (const raw of data.teams) {
                const name = raw.trim();
                if (!name || have.has(name.toLowerCase())) continue;
                try {
                    await apiActionImproved('teams-create', {tournament_uid: uid, name});
                } catch (err: any) {
                    if (err?.code === 'entry_cap') {
                        ui?.cap({cap: err.cap || 8, count: data.teams.length, reason: 'cap'});
                        ui?.step(9);
                        createRunning = false;
                        return;
                    }
                    throw err;
                }
            }

            // Each typed person also gets a real player account, linked to their team — see
            // ensurePlayerProfiles. Whole-team entry names aren't people, so those are skipped.
            if (data.playType !== 'teams') {
                await ensurePlayerProfiles(uid, data);
            }

            // "Get the event pass" plan: collect the $9.99 BEFORE the draw is shown. An already-owned
            // pass is applied silently; otherwise present checkout and resume on return.
            if (sessionStorage.getItem('sp_quiz_plan') === 'pass' && localStorage.getItem(PASS_OK_KEY) !== '1') {
                ui?.status('Checking your Event Pass…');
                const st = await services.eventPass.status().catch(() => null);
                if (st && st.available > 0) {
                    await services.eventPass.apply({tournament_uid: uid}).catch(() => {});
                    localStorage.setItem(PASS_OK_KEY, '1');
                } else {
                    ui?.cap({cap: 8, count: data.teams.length, reason: 'plan'});
                    ui?.step(9);
                    createRunning = false;
                    return;
                }
            }

            // Advanced mode: no auto-draw — hand over to the full Setup page (registration,
            // payments, scheduling, draws — every step configurable).
            if (data.mode === 'advanced') {
                clearGeneratorHandoff();
                [T_UID_KEY, DRAW_DONE_KEY, STARTED_KEY, PASS_OK_KEY, USERS_DONE_KEY].forEach((k) => localStorage.removeItem(k));
                ui?.done(`/tournaments/${uid}/setup`);
                return;
            }

            if (localStorage.getItem(DRAW_DONE_KEY) !== '1') {
                await generateDrawsFor(uid, data);
                localStorage.setItem(DRAW_DONE_KEY, '1');
            }

            clearGeneratorHandoff();
            [T_UID_KEY, DRAW_DONE_KEY, STARTED_KEY, PASS_OK_KEY, USERS_DONE_KEY].forEach((k) => localStorage.removeItem(k));
            sendQuizWelcomeEmail({tournament_uid: uid}); // draw generated + teams in — email the links now

            // Invite-to-earn plan: the draw above is generated and viewable, but scores stay locked
            // until 9+ players sign up — so hand over to Setup → Players, where the progress card,
            // the per-player invites and the registration link live.
            if (sessionStorage.getItem('sp_quiz_plan') === 'earn') {
                createRunning = false;
                suppressSetupTourOnce(); // sent to Players on purpose — the help guide waits
                window.location.assign(`/tournaments/${uid}/setup/applications/`);
                return;
            }

            queuePostCreateOffer(uid); // one-off club over the cover: the event page opens the offer once
            ui?.done(`/tournaments/${uid}/draws?welcome=quiz`);
        } catch (err: any) {
            createRunning = false;
            ui?.status('');
            // The one-off pass gate never reads as an error in a funnel with no offer modal (an
            // already-signed-in one-off organizer can reach it here): hand over to the event page.
            const builtUid = localStorage.getItem(T_UID_KEY);
            if (isPassGate(err) && builtUid) {
                clearGeneratorHandoff();
                [T_UID_KEY, DRAW_DONE_KEY, STARTED_KEY, PASS_OK_KEY, USERS_DONE_KEY].forEach((k) => localStorage.removeItem(k));
                sendQuizWelcomeEmail({tournament_uid: builtUid});
                queuePostCreateOffer(builtUid, err);
                ui?.done(`/tournaments/${builtUid}/draws?welcome=quiz`);
                return;
            }
            const msg = String(err?.message || err?.error || '');
            if (/unauthenticated/i.test(msg)) {
                // Stale/expired session pretended to be logged in — clear it and ask for the
                // account instead of dead-ending on a raw auth error.
                try { await jwt.signOut(); } catch { /* stale token may 401 the logout too */ }
                ui?.step(7);
                return;
            }
            ui?.err(msg || 'Something went wrong — please try again.');
            ui?.step(6);
        }
    };

    // Event Pass checkout: Stripe returns to /quiz/ where the build (or the post-trim recovery)
    // resumes with the pass applied.
    const buyPass = async (fromRecovery = false) => {
        setPayBusy(true);
        recordResponse({plan: 'event_pass'}); // clicked to buy — logged before the Stripe redirect
        try {
            const {redirect_url} = await services.eventPass.checkout('/quiz/', 'quiz50'); // 50% first-pass launch offer
            window.location.href = redirect_url;
        } catch (e: any) {
            setPayBusy(false);
            if (fromRecovery) return; // stay on the done screen — the card's button just re-enables
            setBuilderError(e?.message || 'Could not start checkout.');
            setStep(6);
        }
    };

    // Continue a 9+ build that has just chosen a FREE route at the cap gate (trim to the cap, or
    // invite-to-earn). These skipped the wall on the way in, so they take it now: the Offerwall
    // only evaluates its URL rules on a page view, hence a real navigation — the resume effect
    // re-enters the build from the stash on the other side (signed in, STARTED_KEY still set).
    const continueFree = (h: GeneratorHandoff) => {
        if (h.eventKind !== 'americano' && !onActivatePath) {
            window.location.assign('/quiz/activate/');
            return;
        }
        runCreate(h);
    };

    // Cap gate: skip paying by pledging to get the players signed up. The pledge lifts the entry
    // cap (backend earn-start), so re-running the build adds the whole field; the draw is NOT
    // generated (it stays locked until 9+ entries sign up) and the build lands straight on the
    // event's Setup → Players tab, where EarnFreePassCard tracks progress and holds the unlock.
    const earnInstead = async () => {
        const stash = readGeneratorHandoff();
        const uid = localStorage.getItem(T_UID_KEY);
        if (!stash || !uid) return;
        setPayBusy(true);
        recordResponse({plan: 'earn'}); // chose invites over paying at the cap gate
        try {
            await services.eventPass.earnStart(uid.startsWith('el-') ? {league_uid: uid} : {tournament_uid: uid});
        } catch (e: any) {
            setPayBusy(false);
            setBuilderError(e?.message || 'Could not start the free unlock — please try again.');
            setStep(6);
            return;
        }
        sessionStorage.setItem('sp_quiz_plan', 'earn');
        setPayBusy(false);
        setCapInfo(null);
        continueFree(stash);
    };

    // Or trim to the free cap and finish without paying. This button promises the draw, so it
    // always takes the straight-to-draw path even if Advanced was selected earlier. The cut names
    // aren't discarded: they're kept (with the event uid) so the done screen can offer the pass to
    // restore them.
    const trimToCap = () => {
        const stash = readGeneratorHandoff();
        if (!stash || !capInfo) return;
        // League doubles ranks individuals, so each pair line is TWO entries against the cap.
        const perLine = stash.eventKind === 'league' && stash.leagueFormat === 'doubles_individual' ? 2 : 1;
        const kept = Math.max(1, Math.floor(capInfo.cap / perLine));
        const uid = localStorage.getItem(T_UID_KEY);
        if (uid && stash.teams.length > kept) {
            const rec: RecoveryStash = {uid, handoff: stash, kept, ts: Date.now()};
            try { localStorage.setItem(RECOVERY_KEY, JSON.stringify(rec)); } catch { /* storage off */ }
        }
        const trimmed: GeneratorHandoff = {...stash, teams: stash.teams.slice(0, kept), mode: 'basic'};
        stashGeneratorTournament(trimmed);
        restoreFromHandoff(trimmed); // the done screen counts what actually made it in
        sessionStorage.setItem('sp_quiz_plan', 'free');
        recordResponse({plan: 'free'}); // declined the pass at the cap gate
        setCapInfo(null);
        continueFree(trimmed);
    };

    // Pass was a plan choice (small field) — carry on free without paying.
    const skipPass = () => {
        const stash = readGeneratorHandoff();
        sessionStorage.setItem('sp_quiz_plan', 'free');
        recordResponse({plan: 'free'}); // declined the pass at the cap gate
        setCapInfo(null);
        if (stash) continueFree(stash);
    };

    const pct = Math.round(((Math.min(step, 4) + 1) / 5) * 100);
    const sport = SPORT_NAMES[answers['2']] || 'your sport';
    const eventName = EVENT_NAMES[answers['1']] || 'event';

    const pain = answers['3'];
    const features = [
        PAIN_FEATURES[pain] || 'Live scoring, brackets and standings',
        // Registration / live URL / scheduling are always in the box — skip the ones the
        // pain-matched line above already covers so nothing reads twice.
        ...(pain !== 'signups' ? ['Player registration form — share one link, entries fill themselves in'] : []),
        ...(pain !== 'schedule' ? [`A live ${isLeague ? 'league' : 'tournament'} page with its own public URL`] : []),
        isLeague
            ? 'League standings ranked by win % or points — updated after every score'
            : 'Automatic match scheduling — rounds and courts sorted for you',
        `${sport.charAt(0).toUpperCase() + sport.slice(1)} formats and scoring, pre-configured`,
        'Free iOS & Android app for you and your players',
    ];

    return (
        <div className="sp-quiz">
            <title>Set up your event in 30 seconds — ScorePal</title>
            <meta name="robots" content="noindex" />
            <style>{`
                .sp-quiz { min-height:100dvh; background:linear-gradient(160deg,#0B3DB0 0%,#0A37A0 55%,#06246E 100%);
                    font-family:Inter,sans-serif; color:#fff; display:flex; flex-direction:column; align-items:center;
                    padding:20px 16px 48px; }
                .sp-quiz .wrap { width:100%; max-width:560px; }

                @keyframes spLogoSpin { 0% {transform:rotateY(0) scale(.6); opacity:0} 60% {opacity:1}
                    100% {transform:rotateY(720deg) scale(1); opacity:1} }
                @keyframes spFadeUp { from {opacity:0; transform:translateY(12px)} to {opacity:1; transform:none} }
                .sp-splash { display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:70dvh; text-align:center; }
                .sp-splash .pill { width:180px; height:180px; border-radius:50%; background:#ffffff; display:grid; place-items:center;
                    box-shadow:0 10px 40px rgba(0,0,0,.35), 0 0 0 6px rgba(255,255,255,.12);
                    animation:spLogoSpin 2.8s cubic-bezier(.2,.7,.3,1) both; }
                .sp-splash img { width:118px; height:118px; }
                .sp-splash .word { font-family:'Bebas Neue',sans-serif; font-size:3.2rem; letter-spacing:.06em; margin-top:18px;
                    animation:spFadeUp .6s ease-out 1.6s both; }
                .sp-splash .tag { color:#cddbff; font-size:1.05rem; margin-top:4px; animation:spFadeUp .6s ease-out 2.2s both; }
                .sp-splash .ltrack { width:min(260px,70%); height:6px; margin-top:26px; border-radius:99px;
                    background:rgba(255,255,255,.18); overflow:hidden; animation:spFadeUp .5s ease-out 2.6s both; }
                .sp-splash .lbar { height:100%; width:0; border-radius:99px; background:#F2C41E; animation:spLoad 4.6s ease-in-out 2.7s forwards; }
                @keyframes spLoad { to { width:100% } }
                .sp-splash .lmsg { color:#9db6f0; font-size:.85rem; margin-top:10px; animation:spFadeUp .5s ease-out 3s both; }

                .sp-quiz .qhead { margin:8px 0 18px; animation:spFadeUp .4s ease-out both; }
                .sp-quiz .pmeta { display:flex; justify-content:space-between; font-size:.8rem; color:#cddbff; margin-bottom:6px; }
                .sp-quiz .ptrack { height:8px; border-radius:99px; background:rgba(255,255,255,.18); overflow:hidden; }
                .sp-quiz .pbar { height:100%; border-radius:99px; background:#F2C41E; transition:width .35s ease; }
                .sp-quiz .unlock { margin-top:10px; background:rgba(46,204,113,.16); border:1px solid rgba(46,204,113,.45);
                    color:#c9f7dd; font-size:.85rem; border-radius:10px; padding:8px 12px; animation:spFadeUp .35s ease-out both; }

                .sp-quiz h1 { font-family:'Bebas Neue',sans-serif; font-size:clamp(1.7rem,5.5vw,2.3rem); line-height:1.1;
                    letter-spacing:.02em; margin:6px 0 4px; }
                .sp-quiz .sub { color:#cddbff; font-size:.95rem; margin:0 0 18px; }
                .sp-quiz .qstep { animation:spFadeUp .4s ease-out both; }
                .sp-quiz .opt { display:block; width:100%; text-align:left; background:rgba(255,255,255,.08);
                    border:1.5px solid rgba(255,255,255,.25); color:#fff; border-radius:14px; padding:15px 18px;
                    font-size:1rem; font-weight:600; margin-bottom:10px; cursor:pointer; transition:all .15s ease; }
                .sp-quiz .opt:hover, .sp-quiz .opt:active { background:rgba(255,255,255,.16); border-color:#F2C41E; transform:translateY(-1px); }
                .sp-quiz .opt.sel { background:rgba(242,196,30,.18); border-color:#F2C41E; }
                .sp-quiz .opt .odesc { display:block; font-weight:400; font-size:.82rem; color:#cddbff; margin-top:2px; }

                .sp-quiz label { display:block; font-family:'Bebas Neue',sans-serif; font-size:1.15rem; letter-spacing:.05em;
                    color:#F2C41E; margin:18px 0 7px; }
                .sp-quiz label:first-of-type { margin-top:4px; }
                .sp-quiz .warn { background:rgba(242,196,30,.14); border:1px solid rgba(242,196,30,.5); color:#ffe9a8;
                    font-size:.85rem; border-radius:10px; padding:8px 12px; margin:-6px 0 12px; }
                .sp-quiz .pairs-preview { display:flex; flex-wrap:wrap; gap:6px; margin:-6px 0 12px; }
                .sp-quiz .pair-chip { background:rgba(46,204,113,.16); border:1px solid rgba(46,204,113,.45); color:#c9f7dd;
                    font-size:.8rem; font-weight:600; border-radius:99px; padding:4px 11px; display:inline-flex; align-items:center; gap:6px; }
                .sp-quiz .chip-x { background:none; border:none; color:#c9f7dd; font-size:1rem; font-weight:800;
                    line-height:1; padding:0 2px; cursor:pointer; opacity:.75; }
                .sp-quiz .chip-x:hover { opacity:1; color:#ff9d94; }
                .sp-quiz .tin { width:100%; background:rgba(255,255,255,.1); border:1.5px solid rgba(255,255,255,.3);
                    border-radius:12px; color:#fff; font-size:1rem; padding:12px 14px; margin-bottom:14px; outline:none; }
                .sp-quiz .tin:focus { border-color:#F2C41E; }
                .sp-quiz textarea.tin { min-height:130px; resize:vertical; font-family:Inter,sans-serif; }
                .sp-quiz .err { background:rgba(231,76,60,.18); border:1px solid rgba(231,76,60,.5); color:#ffd4cc;
                    font-size:.85rem; border-radius:10px; padding:8px 12px; margin-bottom:12px; }

                .sp-quiz .feat-card { background:rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.22);
                    border-radius:14px; padding:14px 18px; margin-bottom:18px; }
                .sp-quiz .feat-label { font-size:.72rem; text-transform:uppercase; letter-spacing:.14em; color:#F2C41E; margin:0 0 8px; }
                .sp-quiz .feat { display:flex; gap:8px; font-size:.92rem; margin-bottom:6px; color:#eaf0ff; }
                .sp-quiz .offer-grid { display:grid; gap:12px; }
                @media (min-width:560px) { .sp-quiz .offer-grid { grid-template-columns:1fr 1fr; } }
                .sp-quiz .plan-card { position:relative; background:#fff; color:#0B1B4B; border-radius:16px; padding:20px 18px; }
                .sp-quiz .plan-card.featured { border:2.5px solid #F2C41E; box-shadow:0 8px 28px rgba(0,0,0,.28); }
                .sp-quiz .badge { position:absolute; top:-11px; left:16px; background:#F2C41E; color:#06246E;
                    font-size:.68rem; font-weight:800; text-transform:uppercase; letter-spacing:.08em; border-radius:99px; padding:4px 10px; }
                .sp-quiz .discount { position:absolute; top:-13px; right:-6px; background:#E7333F; color:#fff;
                    font-size:.72rem; font-weight:900; letter-spacing:.04em; border-radius:99px; padding:5px 11px;
                    box-shadow:0 3px 10px rgba(231,51,63,.45); transform:rotate(3deg); }
                .sp-quiz .plan-price .was { font-size:1.3rem; color:#9aa7cc; margin-right:8px; }
                .sp-quiz .plan-offer { font-size:.78rem; font-weight:700; color:#C62835; margin:-2px 0 8px; }
                .sp-quiz .plan-name { font-weight:700; margin:0; }
                .sp-quiz .plan-price { font-family:'Bebas Neue',sans-serif; font-size:2.3rem; margin:2px 0 6px; color:#0B3DB0; }
                .sp-quiz .plan-desc { font-size:.88rem; color:#40507e; margin:0 0 14px; min-height:52px; }
                .sp-quiz .cta { width:100%; border-radius:12px; border:1.5px solid #fff; background:transparent; color:#fff;
                    font-weight:700; font-size:.98rem; padding:13px 0; cursor:pointer; }
                .sp-quiz .cta.primary { background:#F2C41E; color:#06246E; border-color:#F2C41E; }
                .sp-quiz .cta.google { background:#fff; color:#1a1a2e; border-color:#fff; display:flex; align-items:center; justify-content:center; gap:10px; }
                .sp-quiz .or-div { text-align:center; margin:12px 0; color:rgba(255,255,255,.55); font-size:12px; text-transform:uppercase; letter-spacing:.14em; }
                .sp-quiz .plan-card .cta { border-color:#0B3DB0; color:#0B3DB0; background:#fff; }
                .sp-quiz .plan-card .cta.primary { background:#0B3DB0; color:#fff; border-color:#0B3DB0; }
                .sp-quiz .cta:disabled { opacity:.6; }
                .sp-quiz .small-note { font-size:.78rem; color:#b9c8ef; margin-top:10px; text-align:center; }
                .sp-quiz .gc-row { display:flex; gap:8px; }
                .sp-quiz .opt.gc { flex:1; text-align:center; padding:11px 0; margin-bottom:14px; }
                .sp-quiz .trust-head { margin:26px 0 10px; text-align:center; font-size:.75rem; text-transform:uppercase;
                    letter-spacing:.16em; color:#9db6f0; }
                .sp-quiz .dupr-mark { display:flex; align-items:center; justify-content:center; gap:9px; margin:0 0 14px; }
                .sp-quiz .dupr-mark span { font-size:.68rem; font-weight:600; text-transform:uppercase;
                    letter-spacing:.14em; color:rgba(255,255,255,.55); }
                .sp-quiz .dupr-mark img { height:22px; width:auto; opacity:.95; }
                .sp-quiz .stat-row { display:flex; gap:10px; justify-content:center; margin-bottom:14px; }
                .sp-quiz .stat { flex:1; max-width:160px; background:rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.2);
                    border-radius:12px; padding:10px 6px; text-align:center; }
                .sp-quiz .stat .n { display:block; font-family:'Bebas Neue',sans-serif; font-size:1.6rem; line-height:1.1; color:#F2C41E; }
                .sp-quiz .stat .l { display:block; font-size:.68rem; color:#cddbff; margin-top:2px; }
                .sp-quiz .testi { background:rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.2);
                    border-radius:14px; padding:16px 18px; animation:spFadeUp .45s ease-out both; }
                .sp-quiz .tdots { display:flex; gap:6px; justify-content:center; margin-top:12px; }
                .sp-quiz .tdot { width:8px; height:8px; border-radius:50%; border:none; padding:0; cursor:pointer;
                    background:rgba(255,255,255,.3); }
                .sp-quiz .tdot.on { background:#F2C41E; }
                .sp-quiz .testi .tq { font-size:.9rem; font-style:italic; color:#eaf0ff; line-height:1.55; margin:0 0 10px; }
                .sp-quiz .testi .ta { font-size:.85rem; font-weight:700; margin:0; }
                .sp-quiz .testi .ta span { font-weight:400; color:#cddbff; }
                .sp-quiz .spin { display:inline-block; width:20px; height:20px; border-radius:50%; border:3px solid rgba(255,255,255,.3);
                    border-top-color:#F2C41E; animation:spRotate .8s linear infinite; vertical-align:-4px; margin-right:10px; }
                @keyframes spRotate { to { transform:rotate(360deg) } }
                .sp-quiz .prep-wrap { display:flex; flex-direction:column; align-items:center; justify-content:center;
                    min-height:40vh; text-align:center; }
                .sp-quiz .prep-wrap .spin { width:34px; height:34px; border-width:4px; margin:0 0 22px; }
                .sp-quiz .prep-track { width:min(260px,70%); height:6px; border-radius:99px; background:rgba(255,255,255,.18); overflow:hidden; }
                .sp-quiz .prep-bar { height:100%; border-radius:99px; background:#F2C41E; transition:width .55s ease; }
                .sp-quiz .prep-msg { margin-top:14px; font-size:.95rem; color:#cddbff; animation:spFadeUp .4s ease-out both; }
                @media (prefers-reduced-motion: reduce) { .sp-quiz * { animation:none !important; } }
            `}</style>

            <div className="wrap">
                {phase === 'splash' && (
                    <div className="sp-splash">
                        <div className="pill"><img src="/favicon-180x180.png" alt="ScorePal" width={118} height={118} /></div>
                        <p className="word">ScorePal</p>
                        <p className="tag">Let's build your event</p>
                        <div className="ltrack"><div className="lbar" /></div>
                        <p className="lmsg">Loading formats, scoring &amp; scheduling…</p>
                    </div>
                )}

                {phase === 'quiz' && step < 5 && (
                    <>
                        <div className="qhead">
                            <div className="pmeta"><span>Step {step + 1} of 5</span><span>{pct}%</span></div>
                            <div className="ptrack"><div className="pbar" style={{width: `${pct}%`}} /></div>
                            {unlock && <div className="unlock">{unlock}</div>}
                        </div>
                        <div className="qstep" key={step}>
                            <h1>{STEPS[step].q}</h1>
                            <p className="sub">{STEPS[step].sub}</p>
                            {STEPS[step].options.map((o) => (
                                <button key={o.val} className="opt" onClick={() => answer(step + 1, o.val)}>{o.label}</button>
                            ))}
                        </div>
                    </>
                )}

                {phase === 'quiz' && step === 10 && (
                    <div className="qstep">
                        <h1>Your {eventName} setup is ready</h1>
                        <p className="sub">
                            {title || `Your ${sport} ${eventName}`} is live — {teamNames.length} {isLeague ? (leagueFormat === 'teams' ? 'teams' : leagueFormat === 'singles' ? 'players' : 'pairs') : (playType === 'doubles' ? 'pairs' : 'players')} in{mode === 'advanced' ? ', ready to configure' : isLeague ? ' and standings live' : ' and the draw is generated'}.
                        </p>
                        <div className="feat-card">
                            <p className="feat-label">Included in your setup</p>
                            {features.map((f) => <div key={f} className="feat"><span>✓</span><span>{f}</span></div>)}
                        </div>
                        <button className="cta primary" onClick={() => { if (doneDest) window.location.href = doneDest; }}>
                            {mode === 'advanced' ? 'Continue to full setup →' : isLeague ? 'Open my league →' : 'Show my draw →'}
                        </button>

                        {recovery && (() => {
                            const h = recovery.handoff;
                            const lines = h.teams.length - recovery.kept;
                            const isLg = h.eventKind === 'league';
                            const unit = isLg
                                ? (h.leagueFormat === 'teams' ? 'team' : h.leagueFormat === 'singles' ? 'player' : 'pair')
                                : (h.playType === 'doubles' ? 'pair' : h.playType === 'teams' ? 'team' : 'player');
                            const noun = `${lines} ${unit}${lines === 1 ? '' : 's'}`;
                            return (
                                <div className="plan-card featured" style={{marginTop: 20}}>
                                    <span className="badge">Saved from your list</span>
                                    <span className="discount">50% OFF</span>
                                    <p className="plan-name">{noun} didn't make the free cut</p>
                                    <p className="plan-price"><s className="was">$9.99</s> $4.99</p>
                                    <p className="plan-desc">
                                        We kept {lines === 1 ? 'the name' : 'their names'} — one Event Pass adds {lines === 1 ? 'it' : 'them all'} back
                                        {isLg ? ' into your league' : ' and rebuilds the draw'}, with unlimited entries for this event.
                                    </p>
                                    <button className="cta primary" onClick={() => buyPass(true)} disabled={payBusy}>
                                        {payBusy ? 'Opening checkout…' : `Add ${lines === 1 ? `the missing ${unit}` : `all ${noun}`} back — $4.99 →`}
                                    </button>
                                </div>
                            );
                        })()}

                        <p className="trust-head">Trusted by real organisers, players and clubs</p>
                        <div className="dupr-mark">
                            <span>Official DUPR Partner</span>
                            <img src="/DUPR-White.png" alt="DUPR — Official Partner" />
                        </div>
                        {stats && (
                            <div className="stat-row">
                                <div className="stat"><span className="n">{stats.matches.toLocaleString()}+</span><span className="l">Matches played</span></div>
                                <div className="stat"><span className="n">{stats.players.toLocaleString()}+</span><span className="l">Players</span></div>
                                <div className="stat"><span className="n">{(stats.clubs + stats.communities).toLocaleString()}+</span><span className="l">Clubs &amp; communities</span></div>
                            </div>
                        )}
                        <div className="testi" key={tIdx}>
                            <p className="tq">“{TESTIMONIALS[tIdx].quote}”</p>
                            <p className="ta">{TESTIMONIALS[tIdx].name} <span>— {TESTIMONIALS[tIdx].role}</span></p>
                            <div className="tdots">
                                {TESTIMONIALS.map((_, i) => (
                                    <button key={i} className={`tdot${i === tIdx ? ' on' : ''}`} onClick={() => setTIdx(i)} aria-label={`Testimonial ${i + 1}`} />
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {phase === 'quiz' && step === 6 && prepStage !== null && (() => {
                    const entriesNoun = pairEntry ? (isLeague && leagueFormat === 'teams' ? 'teams' : 'pairs') : 'players';
                    const prepMsgs = [
                        `Saving your ${eventName} setup…`,
                        teamNames.length > 0 ? `Placing your ${teamNames.length} ${entriesNoun}…` : 'Preparing your event…',
                        isAmericano ? 'Preparing your Americano…' : isLeague ? 'Getting your standings ready…' : 'Preparing your draw…',
                    ];
                    return (
                        <div className="qstep prep-wrap">
                            <span className="spin" />
                            <div className="prep-track"><div className="prep-bar" style={{width: `${Math.round(((prepStage + 1) / prepMsgs.length) * 100)}%`}} /></div>
                            <p className="prep-msg">{prepMsgs[Math.min(prepStage, prepMsgs.length - 1)]}</p>
                        </div>
                    );
                })()}

                {phase === 'quiz' && step === 6 && prepStage === null && (
                    <div className="qstep">
                        <h1>Set up your {isLeague ? 'league' : 'tournament'}</h1>
                        <p className="sub">{isLeague
                            ? 'Name it, pick a format and add your players — standings update themselves as scores come in.'
                            : 'Name it, pick a format and add your teams — the draw generates itself.'}</p>
                        {builderError && <div className="err">{builderError}</div>}

                        {!isAmericano && <>
                            <label>How do you want to set it up?</label>
                            <button className={`opt${mode === 'basic' ? ' sel' : ''}`} onClick={() => setMode('basic')}>
                                Basic<span className="odesc">{isLeague
                                    ? 'Straight to your league — players in, standings live, record scores right away.'
                                    : 'Straight to your tournament draw — fixtures ready in seconds.'}</span>
                            </button>
                            <button className={`opt${mode === 'advanced' ? ' sel' : ''}`} onClick={() => setMode('advanced')}>
                                Advanced<span className="odesc">Set up each step — registration, connecting payments, scheduling options and more.</span>
                            </button>
                        </>}

                        <label>{isLeague ? 'League name' : isAmericano ? 'Americano name' : 'Tournament name'}</label>
                        <input className="tin" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={isLeague ? 'e.g. Monday Night League' : isAmericano ? 'e.g. Friday Night Americano' : 'e.g. Friday Night Cup'} />

                        {isLeague ? (
                            <>
                                <label>League format</label>
                                <button className={`opt${leagueFormat === 'singles' ? ' sel' : ''}`} onClick={() => setLeagueFormat('singles')}>
                                    Singles<span className="odesc">1v1 matches — players ranked by win % or points.</span>
                                </button>
                                <button className={`opt${leagueFormat === 'doubles_individual' ? ' sel' : ''}`} onClick={() => setLeagueFormat('doubles_individual')}>
                                    Doubles<span className="odesc">Doubles matches with individual rankings — everyone ranked by their own win %.</span>
                                </button>
                                <button className={`opt${leagueFormat === 'teams' ? ' sel' : ''}`} onClick={() => setLeagueFormat('teams')}>
                                    Teams<span className="odesc">Fixed teams ranked on the league table.</span>
                                </button>
                            </>
                        ) : (
                            <>
                        {!isAmericano && <>
                        <label>Singles or doubles?</label>
                        <div className="gc-row">
                            <button className={`opt gc${playType === 'singles' ? ' sel' : ''}`} onClick={() => setPlayType('singles')}>Singles</button>
                            <button className={`opt gc${playType === 'doubles' ? ' sel' : ''}`} onClick={() => setPlayType('doubles')}>Doubles</button>
                        </div>
                        </>}

                        <label>Format</label>
                        {FORMATS.map((f) => (
                            <button key={f.val} className={`opt${format === f.val ? ' sel' : ''}`} onClick={() => setFormat(f.val)}>
                                {f.label}<span className="odesc">{f.desc}</span>
                            </button>
                        ))}

                        {format === 'groups_knockout' && (
                            <>
                                <label>How many groups?</label>
                                <div className="gc-row">
                                    {[2, 4, 8, 16].map((n) => (
                                        <button key={n} className={`opt gc${groupCount === n ? ' sel' : ''}`} onClick={() => setGroupCount(n)}>
                                            {n} groups
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                            </>
                        )}

                        {isAmericano ? (
                            <div className="warn" style={{marginTop: 8}}>
                                Next, on the Americano setup screen, you'll pick your players, courts and points per round —
                                and we'll generate the partner-rotation schedule automatically.
                            </div>
                        ) : (<>
                        <label style={{marginTop: 6}}>
                            Players — one per line{pairEntry ? ' (every two names make a pair)' : ''} — {teamNames.length} {pairEntry ? (isLeague && leagueFormat === 'teams' ? 'teams' : 'pairs') : 'players'}
                            {mode === 'advanced' ? ' — optional, players can register themselves' : ''}
                        </label>
                        <textarea className="tin" value={teamsText} onChange={(e) => setTeamsText(e.target.value)}
                                  placeholder={'Anna\nBen\nCarlos\nDana\n…'} />

                        {teamNames.length > 0 && (
                            <div className="pairs-preview">
                                {teamNames.map((p, i) => (
                                    <span key={i} className="pair-chip">
                                        {p}
                                        <button type="button" className="chip-x" aria-label={`Remove ${p}`}
                                                onClick={() => removeEntry(i)}>×</button>
                                    </span>
                                ))}
                            </div>
                        )}
                        {leftoverSingle && (
                            <div className="warn">
                                <strong>{leftoverSingle}</strong> is waiting for a partner — add one more name below them
                                (or write a pair on one line, e.g. <em>{leftoverSingle} &amp; Ben</em>).
                            </div>
                        )}
                        </>)}

                        <button className="cta primary" onClick={generateDraw}>
                            {isAmericano ? 'Continue to Americano setup →' : mode === 'advanced' ? 'Continue to full setup →' : isLeague ? 'Create my league →' : 'Generate draw →'}
                        </button>
                        {!isAmericano && <p className="small-note">Free — no limit on entries.</p>}
                    </div>
                )}

                {phase === 'quiz' && step === 7 && (
                    <div className="qstep">
                        <h1>Save your {isAmericano ? 'Americano' : isLeague ? 'league' : 'draw'}</h1>
                        <p className="sub">{isAmericano
                            ? 'Create your free account, then pick your players, courts and points to build your Americano.'
                            : <>Create your free account — your {teamNames.length}-{isLeague ? (leagueFormat === 'teams' ? 'team' : leagueFormat === 'doubles_individual' ? 'pair' : 'player') : 'team'} {eventName} is ready to {isLeague ? 'create' : 'generate'}.</>}</p>
                        {suError && <div className="err">{suError}</div>}
                        <button className="cta google" type="button" disabled={suBusy}
                                onClick={() => startGoogleSignIn(isAmericano ? '/quiz/' : '/quiz/activate/')}>
                            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06L5.84 9.9C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
                            Continue with Google
                        </button>
                        <div className="or-div">or</div>
                        <form onSubmit={submitSignup}>
                            <label>Your name</label>
                            <input className="tin" value={suName} onChange={(e) => setSuName(e.target.value)} autoComplete="name" />
                            <label>Email</label>
                            <input className="tin" type="email" value={suEmail} onChange={(e) => setSuEmail(e.target.value)} autoComplete="email" />
                            <label>Password</label>
                            <input className="tin" type="password" value={suPassword} onChange={(e) => setSuPassword(e.target.value)} autoComplete="new-password" />
                            <button className="cta primary" type="submit" disabled={suBusy}>
                                {suBusy ? 'Creating your account…' : isAmericano ? 'Create account & set up Americano →' : isLeague ? 'Create account & create my league →' : 'Create account & generate draw →'}
                            </button>
                        </form>
                        <p className="small-note">By continuing you agree to ScorePal's <a href="/terms-conditions/" target="_blank" rel="noreferrer" style={{color: '#F2C41E'}}>Terms &amp; Conditions</a>.</p>
                        <p className="small-note">Already have an account? <a href="/login/" style={{color: '#F2C41E'}}>Log in</a> — your {isLeague ? 'league' : 'draw'} is saved and will finish when you return here.</p>
                    </div>
                )}

                {phase === 'quiz' && step === 8 && (
                    <div className="qstep" style={{textAlign: 'center', paddingTop: '18dvh'}}>
                        <h1><span className="spin" />{status || `Building your ${isLeague ? 'league' : 'tournament'}…`}</h1>
                        <p className="sub">This only takes a few seconds.</p>
                    </div>
                )}

                {phase === 'quiz' && step === 9 && capInfo && (
                    <div className="qstep">
                        {capInfo.reason === 'cap' ? (
                            <>
                                <h1>Unlock all {capInfo.count} {isLeague && leagueFormat !== 'teams' ? 'players' : 'teams'}</h1>
                                <p className="sub">The free plan covers {capInfo.cap} entries per event — your first {capInfo.cap} are already in.</p>
                            </>
                        ) : (
                            <>
                                <h1>Activate your event pass</h1>
                                <p className="sub">{isLeague
                                    ? 'One payment and your league opens instantly — unlimited players for this event.'
                                    : 'One payment and your draw generates instantly — unlimited teams for this event.'}</p>
                            </>
                        )}
                        <div className="plan-card featured" style={{marginBottom: 12}}>
                            <span className="badge">One-off — no subscription</span>
                            <span className="discount">50% OFF</span>
                            <p className="plan-name">Unlimited event pass</p>
                            <p className="plan-price"><s className="was">$9.99</s> $4.99</p>
                            <p className="plan-offer">One time only — first pass half price!</p>
                            <p className="plan-desc">Unlimited {isLeague && leagueFormat !== 'teams' ? 'players' : 'teams'} for this event. Collect entry fees and it pays for itself with one signup.</p>
                            <button className="cta primary" onClick={() => buyPass()} disabled={payBusy}>
                                {payBusy ? 'Opening checkout…' : `Pay $4.99 & show my ${isLeague ? 'league' : 'draw'} →`}
                            </button>
                        </div>
                        {/* The earned pass is for racquet sports only; the server refuses the pledge otherwise. */}
                        {isRacquetSport(sportKey) && <div className="plan-card featured" style={{marginBottom: 12, borderColor: '#2ECC71'}}>
                            <span className="badge" style={{background: '#2ECC71', color: '#fff'}}>No payment needed</span>
                            <p className="plan-name">Get your event for FREE — invite your players</p>
                            <p className="plan-price" style={{color: '#16a34a'}}>FREE</p>
                            <p className="plan-desc" style={{minHeight: 0}}>
                                Add your full field and invite every player — by email or your event link.
                                {isLeague ? ' Your league is created right away' : ' Your draw is generated right away'} —
                                once 9+ are signed up, recording scores unlocks along with
                                unlimited {isLeague && leagueFormat !== 'teams' ? 'players' : 'teams'}.
                            </p>
                            <button className="cta primary" style={{background: '#2ECC71', borderColor: '#2ECC71', color: '#fff'}}
                                    onClick={earnInstead} disabled={payBusy}>
                                Get players signed up →
                            </button>
                        </div>}
                        {capInfo.reason === 'cap'
                            ? <button className="cta" onClick={trimToCap}>Continue free with {capInfo.cap} {isLeague && leagueFormat !== 'teams' ? 'players' : 'teams'}</button>
                            : <button className="cta" onClick={skipPass}>Continue on the free plan instead</button>}
                    </div>
                )}
            </div>
        </div>
    );
}
